import { Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PortalLifecycleStatus } from "../../../../autogen/bff-api";
import { FilterChip } from "../../../../common/components/FilterChip";

export type LifecycleFilter = PortalLifecycleStatus | "ALL";

export const LIFECYCLE_FILTER_ORDER: PortalLifecycleStatus[] = [
  PortalLifecycleStatus.Open,
  PortalLifecycleStatus.DeadlinePassed,
  PortalLifecycleStatus.UnderEvaluation,
  PortalLifecycleStatus.Awarded,
  PortalLifecycleStatus.Cancelled,
  PortalLifecycleStatus.Concluded,
];

export const useLifecycleFilterLabel = () => {
  const { t } = useTranslation();
  return (status: PortalLifecycleStatus): string => {
    switch (status) {
      case PortalLifecycleStatus.Open:
        return t("Open");
      case PortalLifecycleStatus.DeadlinePassed:
        return t("Deadline passed");
      case PortalLifecycleStatus.UnderEvaluation:
        return t("Under evaluation");
      case PortalLifecycleStatus.Awarded:
        return t("Awarded");
      case PortalLifecycleStatus.Cancelled:
        return t("Cancelled_competition");
      case PortalLifecycleStatus.Concluded:
        return t("Concluded");
    }
  };
};

// Only an invited supplier can meet a concluded competition — the anonymous listing filters those
// statuses out entirely — so the chip would read "(0)" for everyone else.
const shown = (
  status: PortalLifecycleStatus,
  counts: Partial<Record<PortalLifecycleStatus, number>>,
  active: LifecycleFilter,
): boolean =>
  status !== PortalLifecycleStatus.Concluded || (counts[status] ?? 0) > 0 || active === status;

export const LifecycleFilterChips = ({
  active,
  counts,
  onChange,
}: {
  active: LifecycleFilter;
  counts: Partial<Record<PortalLifecycleStatus, number>>;
  onChange: (filter: LifecycleFilter) => void;
}) => {
  const { t } = useTranslation();
  const label = useLifecycleFilterLabel();
  return (
    <Wrap spacing="2" role="group" aria-label={t("Competition status") ?? "Competition status"}>
      <WrapItem>
        <FilterChip active={active === "ALL"} label={t("All")} onClick={() => onChange("ALL")} />
      </WrapItem>
      {LIFECYCLE_FILTER_ORDER.filter((status) => shown(status, counts, active)).map((status) => (
        <WrapItem key={status}>
          <FilterChip
            active={active === status}
            label={`${label(status)} (${counts[status] ?? 0})`}
            onClick={() => onChange(status)}
          />
        </WrapItem>
      ))}
    </Wrap>
  );
};
